// A doctor's or clinic admin's own TEST mother (onboarding step 3). The server makes it and places the calls
// (functions/src/calling/engine.ts: addTestMother, testCallMe); the demo only pretends.
import { deleteField, doc, updateDoc } from 'firebase/firestore';
import type { Mother } from '../types';
import { db } from '../lib/firebase';
import { callServer } from './server';
import { isReal } from './store';
import { scope } from './scope';
import { mothers } from './mockData';
import { respond } from './respond';
import { addAudit } from './auditService';

export type TestKind = 'baby' | 'pregnancy';

/** "+91 98••• ••210": a phone partly hidden. Same as maskPhone on the server. */
export function maskTestPhone(phone: string): string {
  const d = phone.replace(/\D/g, '');
  if (d.length < 8) return '••••';
  const india = d.startsWith('91') && d.length === 12;
  const rest = india ? d.slice(2) : d;
  return `${india ? '+91 ' : '+'}${rest.slice(0, 2)}••• ••${rest.slice(-3)}`;
}

// Demo only: the number typed is kept in this browser tab, partly hidden, never stored or sent anywhere.
let demoLast: MyTestStatus['lastCall'] = null;

export async function addTestMother(kind: TestKind, phone: string, myName: string, facilityId: string): Promise<{ id: string }> {
  if (isReal()) return callServer<{ id: string }>('addTestMother', { kind, phone });
  demoLast = null;
  const id = 'test-demo-me';
  const existing = mothers.findIndex((m) => m.id === id);
  if (existing >= 0) mothers.splice(existing, 1);
  const first = myName.replace(/^(dr\.?)\s+/i, '').split(/\s+/)[0] ?? '';
  const today = new Date().toISOString().slice(0, 10);
  const m: Mother = {
    id, facilityId, name: `TEST ${first}`.trim(), phone: '+91 55500 99999', stage: kind, testOnly: true, archived: false,
    ...(kind === 'pregnancy' ? { lmp: new Date(Date.now() - 83 * 864e5).toISOString().slice(0, 10) } : {}),
    consent: { given: true, date: today, version: 3 }, consentStatus: 'yes', consentChannel: 'paper', consentTextVersion: 'test-self',
    optedOut: false, highRisk: false, uhid: 'TEST', callLanguage: 'hi', usualAnswerer: 'mother',
    demoMaskedPhone: maskTestPhone(phone),
  };
  mothers.push(m);
  return respond({ id });
}

export interface TestCallResult { placed: number; failed: number; wouldCall: number; clinicBlocked: string | null; skipped: Record<string, number>; failReason?: string; demo?: boolean; draftVersion?: number }

/** Ring my own TEST mother. With `draft`, Asha uses the not-yet-approved DRAFT for that call type (Test mode only). */
export async function testCallMe(opts: { draft?: boolean; agent?: TestKind; demoVersion?: number } = {}): Promise<TestCallResult> {
  if (isReal()) return callServer<TestCallResult>('testCallMe', { draft: opts.draft, agent: opts.agent });
  // Demo: no call is made; a pretend result shows how the box reports a test.
  demoLast = { id: 'demo-test-call', startedAt: new Date().toISOString(), outcome: 'confirmed', draftVersion: opts.draft ? opts.demoVersion ?? 1 : null, hasRecording: false };
  return respond({ placed: 0, failed: 0, wouldCall: 1, clinicBlocked: null, skipped: {}, demo: true }, 400);
}

export interface MyTestStatus {
  mode: string;
  hasTestMother: boolean;
  stage?: TestKind;
  /** Partly hidden, e.g. "+91 98••• ••210". The full number never reaches the screen. */
  maskedPhone?: string;
  onTestList?: boolean;
  lastCall?: { id: string; startedAt: string; outcome: string; draftVersion: number | null; hasRecording: boolean } | null;
}


/** My own TEST phone, whether it's on the test list, and my latest test call (for the "Test my draft" box). */
export async function myTestStatus(): Promise<MyTestStatus> {
  if (isReal()) return callServer<MyTestStatus>('myTestStatus', {});
  const m = mothers.find((x) => x.id === 'test-demo-me' && !x.archived);
  if (!m) return respond({ mode: 'test', hasTestMother: false });
  return respond({ mode: 'test', hasTestMother: true, stage: m.stage as TestKind, maskedPhone: m.demoMaskedPhone || maskTestPhone(m.phone), onTestList: true, lastCall: demoLast });
}

/**
 * Archive a TEST mother: hidden everywhere, never called. Her phone number is deleted (it is the doctor's or admin's
 * own personal number); the record, its calls and the audit log are kept.
 */
export async function archiveTestMother(m: Mother): Promise<void> {
  if (!m.testOnly) throw new Error('Only TEST mothers can be archived');
  const at = new Date().toISOString();
  if (isReal()) {
    const s = scope();
    await updateDoc(doc(db!, 'facilities', s.facilityId, 'mothers', m.id), { archived: true, archivedAt: at, archivedBy: s.uid ?? '', phone: deleteField() });
    addAudit(s.actor, 'patient.test_archived', `Patient ${m.id}`, s.facilityId);
    return;
  }
  const x = mothers.find((y) => y.id === m.id);
  if (x) Object.assign(x, { archived: true, archivedAt: at, phone: '' });
  await respond(null);
}
